import type { ElementType, ReactNode } from 'react'

type SectionProps = {
  children: ReactNode
  /** Renders as <section> by default; pass 'div' when nesting inside one. */
  as?: ElementType
  id?: string
  className?: string
  /** Drop the horizontal max-width for full-bleed bands. */
  bleed?: boolean
  /** 'ink' shows the page wash; 'surface' draws the tinted gradient band. */
  tone?: 'ink' | 'surface' | 'none'
  labelledBy?: string
}

/* The vertical rhythm and the content width, in one place. Sections that need
   something else should say so with className rather than re-deriving it.

   The 1320px measure and px-5 / sm:px-8 gutters match the sections already
   shipped — kept over the roadmap's 1400 / px-6 so the site stays on one
   grid instead of two. */
function Section({
  children,
  as: Tag = 'section',
  id,
  className = '',
  bleed = false,
  tone = 'none',
  labelledBy,
}: SectionProps) {
  /* 'ink' paints nothing: the page wash on <body> is the ink ground now, and
     a white fill over it would punch a flat hole in the gradient.

     A tinted band always carries its own hairlines. Without them two bands of
     the same value run together, and the call site should not have to
     remember to draw the edge of a background it did not choose. */
  const background =
    tone === 'ink'
      ? ''
      : tone === 'surface'
        ? 'border-y border-line band-surface'
        : ''

  return (
    <Tag
      id={id}
      aria-labelledby={labelledBy}
      className={`${background} py-20 sm:py-28 ${className}`}
    >
      <div
        className={
          bleed ? '' : 'mx-auto w-full max-w-[1320px] px-5 sm:px-8'
        }
      >
        {children}
      </div>
    </Tag>
  )
}

export default Section
